import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { TrackJS } from 'trackjs';
import { v4 as uuidv4 } from 'uuid';

import {
  ENVIRONMENT,
  APP_VERSION,
} from 'src/app/shared/constants/configObjectProperties';
import { ALERT_VIEWED } from 'src/app/shared/dataAnalytics/schema/constants';
import type { Feature } from 'src/app/shared/featureToggle/types';
import { getConfig } from 'src/app/utils/config';
import { isStaging, isProd } from 'src/app/utils/environment';
import globals from 'src/app/utils/globals';
import { actionTrackError } from 'src/app/v1/state/modules/tealium/trackError';
import { store } from 'src/app/v1/state/store';
import type { TrackingEvent } from 'src/types/trackingData';

import { getErrorMessage } from './utils/get-error-message';

export const log = {
  error: (message: string, meta?: any, shouldFireTealium?: boolean) => {
    TrackJS.track({ message, meta });
    datadogLogger.error(message, meta);

    if (shouldFireTealium) {
      const payload: TrackingEvent = {
        eventName: ALERT_VIEWED,
        errorMessage: message,
        data: meta,
        isTrackedError: true,
      };
      store.dispatch(actionTrackError(payload));
    }

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.error(message, meta);
    }
  },
  info: (...message: unknown[]) => {
    TrackJS.console.info(...message);
    datadogLogger.info(...message);

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.info(...message);
    }
  },
  warn: (...message: unknown[]) => {
    TrackJS.console.warn(...message);
    datadogLogger.warn(...message);

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(...message);
    }
  },
  trace: (...message: unknown[]) => {
    // For debugging/tracing events that we don't want to go to TrackJS
    // TrackJS.console.log(...message);
    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.debug(...message);
    }
  },
};

export const loadDatadog = () => {
  if (isProd() || isStaging()) {
    datadogRum.init({
      applicationId: '8a39bca4-cf74-4685-90a7-1ab07a9275a2',
      clientToken: 'pubdccddf693dd56ce37f3bc20b4a22021d',
      env: getConfig(ENVIRONMENT),
      site: 'datadoghq.com',
      service: 'express-ui',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      version: getConfig(APP_VERSION),
      enableExperimentalFeatures: ['feature_flags'],
      allowedTracingUrls: [
        /https:\/\/talent\.seek\.com\.au(?!\/graphql).*/,
        /https:\/\/talent\.seek\.co\.nz(?!\/graphql).*/,
        /https:\/\/talent\.staging\.seek\.com\.au(?!\/graphql).*/,
        /https:\/\/talent\.staging\.seek\.co\.nz(?!\/graphql).*/,
        // Asia Domains
        /https:\/\/ph\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/ph\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/th\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/th\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/my\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/my\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/hk\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/hk\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/id\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/id\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/sg\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/sg\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /http:\/\/hirer-graphql-api-.*\.hirer\.dev\.outfra\.xyz/,
      ],
      defaultPrivacyLevel: 'mask-user-input',
    });

    datadogLogs.init({
      clientToken: 'pub8fe228b74adad734c70245f18a18eddd',
      site: 'datadoghq.com',
      service: 'express-ui',
      env: getConfig(ENVIRONMENT),
      version: getConfig(APP_VERSION),
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
    });

    const appUuid = uuidv4();
    addDatadogGlobalContext('appUuid', appUuid);

    datadogRum.startSessionReplayRecording();
  }
};

const addDatadogGlobalContext = (
  key: string,
  value: string | Record<string, unknown>,
) => {
  datadogRum.setGlobalContextProperty(key, value);
};

const setDatadogUser = (seekAdvertiserId: string) => {
  datadogRum.setUser({ id: seekAdvertiserId });
  addDatadogGlobalContext('SeekAdvertiserId', seekAdvertiserId);
};

export const addFeatureFlag = (feature: Feature) => {
  datadogRum.addFeatureFlagEvaluation(feature.name, feature.enabled);
};

export const installTrackJS = () => {
  const isProduction = getConfig(ENVIRONMENT) === 'production';
  if (!TrackJS.isInstalled()) {
    // https://docs.trackjs.com/browser-agent/sdk-reference/agent-config
    TrackJS.install({
      application: isProduction
        ? 'express-caja-ui-prod'
        : 'job-posting-ui-dev-stgn',
      token: 'c0dee68c9f294d4d8011e8e45b0a74ec',
      console: {
        error: isProduction, // Only production should capture + send console recorded errors
        watch: ['log', 'info', 'warn', 'error'], // Ignore 'debug' console events
      },
      // Note: `serialize` is deprecated, however still functioning and enables
      // us to serialize and deserialize error messages effectively in order to
      // assign meta data to the TrackJS events. If TrackJS provided a `deserialize`
      // method then we would go back to TrackJS's inbuilt `serialize` method.
      // https://docs.trackjs.com/browser-agent/sdk-reference/agent-config/#serialize
      serialize: (error) => {
        try {
          return JSON.stringify(error);
        } catch (err) {
          return 'Unable to serialize error';
        }
      },
      onError(payload, error) {
        const isCypress = Boolean(globals.window.Cypress);
        const shouldSendToTrackJS = !isCypress;
        if (!shouldSendToTrackJS) {
          return false;
        }

        let message = getErrorMessage(error) || 'Unknown Error';
        let meta: any = {};
        try {
          const parsed = JSON.parse(error?.message || '{}');
          message = getErrorMessage(parsed) || message;
          meta = parsed.meta || meta;
        } catch (_ignore) {
          // Ignore any message that can't be JSON.parse'd - it likely isn't JSON and just a console.error
        }

        // Update Error Message
        payload.message = message;

        // Update Custom Meta Data
        Object.keys(meta).forEach((key: string) => {
          try {
            const rawValue = meta[key];
            // NOTE: For error value we only want the name and message (omit the stack trace)
            const value = JSON.stringify(
              key === 'error' && rawValue
                ? { name: rawValue.name, message: rawValue.message }
                : rawValue,
            );
            payload.metadata.push({ key, value });
          } catch (_ignore) {
            // Ignore any meta data that can't be JSON.stringify'd
          }
        });

        // Use error stacktrace if provided
        payload.stack = meta?.error?.stack || payload.stack;

        return true;
      },
    });
  }
};

export const setUser = (seekAdvertiserId: string) => {
  setTrackJSUser(seekAdvertiserId);
  setDatadogUser(seekAdvertiserId);
};

export const setExperiment = (testingId: string) => {
  setTrackJSExperiment(testingId);
  addDatadogGlobalContext('Experiment', testingId);
};

interface DataDogLogContext extends Record<string, unknown> {
  message?: string;
  activity?: string;
}

const mapLogInput: (
  message?: unknown,
  context?: unknown,
) => { message: string; context?: DataDogLogContext } = (message, context) => ({
  message: (message as string | undefined) ?? 'log',
  context: context as DataDogLogContext,
});

const datadogLogger = {
  info: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);

    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      message,
      level: 'info',
    });
  },
  error: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);
    const error = context?.error ?? new Error(message);
    datadogRum.addError(error, {
      ...context,
      message,
      level: 'error',
    });
  },
  warn: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);

    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      message,
      level: 'warn',
    });
  },
};

const setTrackJSUser = (seekAdvertiserId: string) => {
  if (TrackJS.isInstalled()) {
    TrackJS.addMetadata('SeekAdvertiserId', seekAdvertiserId);
  }
};

const setTrackJSExperiment = (testingId: string) => {
  if (TrackJS.isInstalled()) {
    TrackJS.addMetadata('Experiment', testingId);
  }
};
